/**
 * 발주서 작성/수정 모달.
 *
 * 견적서 폼과 매우 유사. 차이:
 *   - 수신 = 매입처 (purchase.supplier distinct 자동완성)
 *   - 참조 = 매입처 담당자 (직접 입력)
 *   - 추가 필드: 납품장소, 납품기한, 결제조건(기본='익월말 현금결제')
 *   - 품목 자동완성 없음 (직접 입력 — 견적서에서 복붙)
 *   - 영업건 매출번호 (선택) — Phase A 단순 텍스트 입력, Phase C 에서 강화
 *   - 기존 발주서 복사(prefill · 「이 내용으로 새 발주서」 · v2.213.0) — 발주일자 오늘 · 영업건 비움([같은 영업건으로]) ·
 *     결제조건은 매입처를 바꾸기 전까지 원본 그대로 · 견적서 첨부 파일은 복사 안 함 · 담당은 쓰는 사람(v2.213.2)
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  computePoTotals,
  DEFAULT_PAYMENT_TERM,
  fetchPoContactDefaults,
  fetchPoItems,
  fetchPoSuppliers,
  lookupPoPaymentTerm,
  type PoCompany,
  type PoContactDefaults,
  type PoDetail,
  type PoDraft,
  type PoItemInput,
  type PoLookupItem,
  type PoType,
} from '../../lib/po-api';
import { SimpleAutocomplete } from '../../components/SimpleAutocomplete';
import { SegmentedControl } from '../../components/ui/SegmentedControl';
import { listPartnerCompanies, partnerNames } from '../../lib/lookup-api';
import { PoItemSearchSelect } from '../../components/PoItemSearchSelect';
import { AttachmentSection, type AcceptSpec } from '../../components/AttachmentSection';
import { SalesPickerModal } from '../../components/SalesPickerModal';
import { QuoteImportModal } from './QuoteImportModal';
import type { ImportedQuote } from '../../lib/quote-import';

// 견적서 첨부형 — 거래처 견적서 파일(PDF 권장, 엑셀/이미지 가능)
const QUOTE_ACCEPT: AcceptSpec = {
  exts: /\.(pdf|xlsx|xls|png|jpe?g)$/i,
  attr: '.pdf,.xlsx,.xls,.png,.jpg,.jpeg,application/pdf',
  label: 'PDF·엑셀·이미지',
};

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  initial?: PoDetail | null;
  /**
   * 새 발주서에 미리 채울 내용 — 기존 발주서 복사(「이 내용으로 새 발주서」 · v2.213.0).
   * mode='create' 일 때만 본다. 발주일자는 오늘 · 관련 영업건은 비움(원본 번호는 [같은 영업건으로] 제안).
   */
  prefill?: PoDraft | null;
  onClose: () => void;
  onSubmit: (
    data: {
      companyId: PoCompany;
      poDate: string;
      poType: PoType;
      totalAmount?: number;
      recipientSupplier: string;
      reference: string;
      projectName: string;
      deliveryPlace: string;
      deliveryDue: string;
      paymentTerm: string;
      linkedSalesId: string | null;
      /** 관련 영업건 전부 — 첫 번째 = 대표 (v2.284.0 · 개발요청 #5) */
      linkedSalesIds: string[];
      remark: string;
      empName: string;
      empTitle: string;
      contactPhone: string;
      contactEmail: string;
      items: PoItemInput[];
    },
    /** 견적서 첨부형 신규 작성 시 업로드 대기 파일 (수정 모드는 즉시 업로드되어 빈 배열) */
    pendingAttachments: File[],
  ) => Promise<void>;
}

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function emptyItem(): PoItemInput {
  return { itemName: '', description: '', qty: 1, unitPrice: 0, note: '' };
}

/** 여러 줄 칸 높이를 내용에 맞춘다 (견적서 품목 칸과 같은 방식) */
function fitHeight(el: HTMLTextAreaElement): void {
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
}

export function PurchaseOrderFormModal({ open, mode, initial, prefill, onClose, onSubmit }: Props) {
  const [companyId, setCompanyId] = useState<PoCompany>('SM');
  const [poDate, setPoDate] = useState<string>(todayIso());
  const [poType, setPoType] = useState<PoType>('standard');
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [pendingAttachments, setPendingAttachments] = useState<File[]>([]);
  const [recipientSupplier, setRecipientSupplier] = useState('');
  const [reference, setReference] = useState('');
  const [projectName, setProjectName] = useState('');
  const [deliveryPlace, setDeliveryPlace] = useState('본사 사무실');
  const [deliveryDue, setDeliveryDue] = useState('협의');
  const [paymentTerm, setPaymentTerm] = useState('익월말 현금결제');
  const [linkedSalesId, setLinkedSalesId] = useState<string>('');
  /** 대표 말고 더 고른 영업건(v2.284.0 · 개발요청 #5 「여러 영업건의 품목이 한 발주서에」) — 순서대로 */
  const [moreSales, setMoreSales] = useState<string[]>([]);
  const [remark, setRemark] = useState('');
  const [items, setItems] = useState<PoItemInput[]>([emptyItem()]);
  const [contact, setContact] = useState<PoContactDefaults | null>(null);
  // 담당/연락처/이메일 — 자동 채움이 기본, 사용자가 수동 수정 가능
  const [empDisplay, setEmpDisplay] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [supplierOptions, setSupplierOptions] = useState<string[]>([]);
  // 품목 검색 후보 (품명&품목 마스터, 매입처 지정 시 그 매입처 매입 품목만)
  const [itemCandidates, setItemCandidates] = useState<PoLookupItem[]>([]);
  const [itemsSupplierFiltered, setItemsSupplierFiltered] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [linkedSalesLabel, setLinkedSalesLabel] = useState<string>('');
  /**
   * 영업건에서 **자동으로 채워 넣은** 프로젝트명 (v2.163.0).
   * 사람이 손으로 적은 값은 절대 덮어쓰지 않되, **자동으로 채운 값은 다른 영업건을 고르면 따라 바뀌게** 하려고
   * 「내가 넣은 값」을 기억해 둔다. (운영 발주서를 보니 사람들은 영업건 프로젝트를 가져다 기관명을 붙이거나
   *  「견적의 건」을 떼는 식으로 **손질해서** 쓴다 — 그 손질을 지우면 안 된다.)
   */
  const autoProjectRef = useRef<string>('');
  const [pickerOpen, setPickerOpen] = useState(false);
  /** 영업건 찾기 창을 「대표 고르기」로 열었나 「하나 더 추가」로 열었나 */
  const [pickerAdd, setPickerAdd] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // 결제조건이 매입처 등록값에서 자동으로 채워졌는지 (화면 안내용)
  const [termAuto, setTermAuto] = useState<string | null>(null);
  /**
   * 복사해 온 발주서의 매입처 (v2.213.0) — 매입처를 **이 값에서 바꾸기 전까지는** 결제조건을 다시 채우지 않는다.
   *   손으로 고친 결제조건이 많아서(운영 28장 중 13장) 복사 때 등록값·기본값으로 덮으면 원본 조건이 사라진다.
   *   ⚠판정은 **타이머가 울릴 때** 한다 — 창을 열 때 결제조건 효과가 초기화 효과보다 먼저 돌아(선언 순서),
   *   지난번 창에 남아 있던 매입처로 예약된 채우기가 뒤늦게 원본 조건을 덮을 수 있다.
   */
  const termKeepSupplierRef = useRef<string | null>(null);
  // 결제조건이 복사 원본의 것인지 (화면 안내용) — 매입처를 바꾸거나 직접 고치면 뗀다
  const [termFromCopy, setTermFromCopy] = useState(false);

  /**
   * 「📋 견적서 자동 채움」 적용 — 숫자·매입처 이름은 모달(lib/quote-import)이 이미 정리해서 넘긴다 (v2.213.1).
   *   매입처가 복사 원본과 같은 회사면 모달이 지금 이름 그대로 넘겨서 원본 결제조건이 지켜진다.
   */
  function applyImported(q: ImportedQuote) {
    if (q.recipientSupplier) setRecipientSupplier(q.recipientSupplier);
    if (q.reference) setReference(q.reference);
    /* 프로젝트명은 **칸이 비어 있을 때만** — 견적서의 건명은 매입처가 붙인 이름이라, 사람이 적었거나 영업건에서
     *   가져온 우리 이름을 덮지 않는다(v2.163.0 과 같은 원칙). 채운 값은 「자동으로 채운 값」으로 쳐서
     *   뒤에 영업건을 고르면 그 영업건 이름으로 바뀐다. */
    if (q.projectName && !projectName.trim()) {
      setProjectName(q.projectName);
      autoProjectRef.current = q.projectName;
    }
    if (q.items.length > 0) {
      setItems(
        q.items.map((it) => ({
          itemName: it.itemName,
          description: it.description,
          qty: it.qty,
          unitPrice: it.unitPrice,
          // 견적서 비고는 여러 줄일 수 있다(v2.325.1) — 발주서 비고는 한 줄 칸이라 줄바꿈을 띄어쓰기로
          note: it.note.replace(/\s*\n\s*/g, ' ').trim(),
        })),
      );
      // 견적서 첨부형이면 품목 표가 숨어 있어 「채웠는데 안 보인다」 — 품목 직접 입력으로 바꾼다
      if (poType === 'quote_attached') setPoType('standard');
    }
  }

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [c, sup, partners] = await Promise.all([
          fetchPoContactDefaults(),
          fetchPoSuppliers(),
          listPartnerCompanies(),
        ]);
        setContact(c);
        // 거래처 마스터(매입처+양쪽) 우선 + 발주 이력 distinct 병합
        setSupplierOptions(Array.from(new Set([...partnerNames(partners, 'supplier'), ...sup])));
      } catch (e) {
        setErr((e as Error).message);
      }
    })();
  }, [open]);

  // 품목 검색 후보 로드 — 매입처(수신)가 바뀌면 그 매입처의 매입 품목으로 재조회.
  // 매입처를 타이핑하는 동안 과도한 호출을 막기 위해 300ms 디바운스.
  useEffect(() => {
    if (!open) return;
    const supplier = recipientSupplier.trim();
    let cancelled = false;
    setItemsLoading(true);
    const t = setTimeout(() => {
      fetchPoItems(supplier || undefined)
        .then((res) => {
          if (cancelled) return;
          setItemCandidates(res.items);
          setItemsSupplierFiltered(res.supplierFiltered);
        })
        .catch(() => {
          if (cancelled) return;
          setItemCandidates([]);
          setItemsSupplierFiltered(false);
        })
        .finally(() => {
          if (!cancelled) setItemsLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [open, recipientSupplier]);

  /**
   * 발주서 수신(매입처)이 바뀌면 그 매입처에 등록된 결제조건으로 채운다 (2026-07-29).
   *   [💳 발주처 결제조건]에 등록이 없으면 기본값. 표기 차이는 서버(norm_company)가 흡수한다.
   *   ⭐사용자 확정: **항상 매입처 기준으로 다시 채운다** — 손으로 고쳐 둔 값도 매입처를 바꾸면 덮어쓴다.
   *   ⚠수정(edit) 모드에서는 저장된 값을 건드리지 않는다.
   */
  useEffect(() => {
    if (!open || mode !== 'create') return;
    const supplier = recipientSupplier.trim();
    let cancelled = false;
    /** 복사 원본의 매입처 그대로인가 — 그렇다면 원본 결제조건을 지킨다 (v2.213.0) */
    const keepCopied = (): boolean =>
      termKeepSupplierRef.current !== null && termKeepSupplierRef.current === supplier;
    // 매입처가 복사 원본과 달라졌다 = 사람이 매입처를 바꿨다 → 이제부터는 늘 매입처 기준(위 사용자 확정)
    if (termKeepSupplierRef.current !== null && !keepCopied()) {
      termKeepSupplierRef.current = null;
      setTermFromCopy(false);
    }
    if (!supplier) {
      if (keepCopied()) return;
      setPaymentTerm(DEFAULT_PAYMENT_TERM);
      setTermAuto(null);
      return;
    }
    const t = setTimeout(() => {
      if (keepCopied()) return;
      lookupPoPaymentTerm(supplier)
        .then((res) => {
          if (cancelled || keepCopied()) return;
          setPaymentTerm(res.paymentTerm ?? DEFAULT_PAYMENT_TERM);
          setTermAuto(res.paymentTerm ? (res.matchedSupplier ?? supplier) : null);
        })
        .catch(() => {
          // 조회 실패는 조용히 기본값 — 발주서 작성 자체를 막을 이유가 없다
          if (cancelled || keepCopied()) return;
          setPaymentTerm(DEFAULT_PAYMENT_TERM);
          setTermAuto(null);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [open, mode, recipientSupplier]);

  // 신규 작성 + contact 자동 채움 (사용자 수동 수정 전 1회)
  useEffect(() => {
    if (!open || mode !== 'create' || !contact) return;
    setEmpDisplay((cur) => cur || contact.displayName);
    setContactPhone((cur) => cur || contact.phone);
    setContactEmail((cur) => cur || contact.email);
  }, [open, mode, contact]);

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && initial) {
      setCompanyId(initial.companyId);
      setPoDate(initial.poDate);
      // 옛 발주서(legacy_pdf)는 이 창으로 열리지 않는다(목록·미리보기에서 ✏ 없음 · 서버 400) — 혹시 와도 일반형으로
      setPoType(initial.poType === 'quote_attached' ? 'quote_attached' : 'standard');
      setTotalAmount(Number(initial.totalWithVat) || 0);
      setPendingAttachments([]);
      setRecipientSupplier(initial.recipientSupplier);
      setReference(initial.reference);
      setProjectName(initial.projectName);
      setDeliveryPlace(initial.deliveryPlace);
      setDeliveryDue(initial.deliveryDue);
      setPaymentTerm(initial.paymentTerm);
      setLinkedSalesId(initial.linkedSalesIds?.[0] ?? initial.linkedSalesId ?? '');
      setMoreSales((initial.linkedSalesIds ?? []).slice(1));
      setLinkedSalesLabel('');
      setRemark(initial.remark);
      setEmpDisplay(initial.empDisplay ?? '');
      setContactPhone(initial.contactPhone ?? '');
      setContactEmail(initial.contactEmail ?? '');
      setItems(
        initial.items.map((it) => ({
          itemName: it.itemName,
          description: it.description,
          qty: it.qty,
          unitPrice: it.unitPrice,
          note: it.note,
        })),
      );
    } else if (mode === 'create') {
      /* prefill = 기존 발주서 복사(「이 내용으로 새 발주서」 · v2.213.0). 없으면 빈 폼.
       *   발주일자는 늘 오늘 · 관련 영업건은 늘 비움(원본 번호는 아래 [같은 영업건으로] 제안) ·
       *   담당/연락처/이메일은 늘 비우고 위 contact 자동 채움이 **쓰는 사람**으로 채운다(v2.213.2 — 운영 28장 모두
       *   담당 = 쓴 사람. 원본 담당을 두면 남의 발주서를 복사할 때 엉뚱한 이름·전화가 매입처로 나간다).
       *   ⚠contact 효과가 이 효과보다 앞에 선언돼 있어도 괜찮다 — 여기서 비운 뒤 창을 열 때 새로 받아 온 contact 로 다시 돈다. */
      const p = prefill ?? null;
      setCompanyId(p?.companyId ?? 'SM');
      setPoDate(todayIso());
      setPoType(p?.poType ?? 'standard');
      setTotalAmount(p?.totalAmount ?? 0);
      setPendingAttachments([]);
      setRecipientSupplier(p?.recipientSupplier ?? '');
      setReference(p?.reference ?? '');
      setProjectName(p?.projectName ?? '');
      setDeliveryPlace(p ? p.deliveryPlace : '본사 사무실');
      setDeliveryDue(p ? p.deliveryDue : '협의');
      setPaymentTerm(p ? p.paymentTerm : DEFAULT_PAYMENT_TERM);
      setLinkedSalesId('');
      setMoreSales([]);
      setLinkedSalesLabel('');
      setRemark(p?.remark ?? '');
      setEmpDisplay('');
      setContactPhone('');
      setContactEmail('');
      setItems(p && p.items.length > 0 ? p.items.map((it) => ({ ...it })) : [emptyItem()]);
    }
    const copying = mode === 'create' && !!prefill;
    // 복사면 원본 매입처를 기억해 결제조건을 지킨다(위 효과) — 아니면 늘 매입처 기준
    termKeepSupplierRef.current = copying ? prefill!.recipientSupplier.trim() : null;
    setTermFromCopy(copying);
    setTermAuto(null);
    /* 창을 열 때마다 「내가 채운 값」 기억을 새로 한다 — 지난 번 것이 남아 이번 입력을 덮으면 안 된다.
     * ⭐복사해 온 프로젝트명은 「내가 채운 값」으로 친다 — 다른 영업건을 고르면 그 영업건 프로젝트명으로 바뀐다
     *   (다른 건으로 다시 쓰는데 옛 프로젝트명이 그대로 매입처에 나가면 안 된다). 손으로 고쳤으면 그대로 둔다. */
    autoProjectRef.current = copying ? prefill!.projectName.trim() : '';
    setErr(null);
  }, [open, mode, initial, prefill]);

  const totals = useMemo(() => computePoTotals(items), [items]);

  /* 품목 「상세 내용」 칸 높이를 내용에 맞춘다 — 칠 때뿐 아니라 **채워질 때도**(복사·수정·견적서 자동 채움).
   *   전에는 칠 때만 맞춰서 여러 줄 상세가 첫 줄만 보였다 → 복사해 온 사람이 「둘째 줄이 안 따라왔나」 오해한다 (v2.213.0).
   *   ⚠rAF 가 아니라 layout effect — 그려지지 않는 창에서도 돈다. */
  const itemsBodyRef = useRef<HTMLTableSectionElement>(null);
  useLayoutEffect(() => {
    if (!open) return;
    itemsBodyRef.current?.querySelectorAll('textarea').forEach((el) => fitHeight(el));
  }, [open, items, poType]);

  if (!open) return null;

  /** 복사해 온 발주서의 원본 영업건 — [같은 영업건으로] 제안용 (새 작성일 때만) */
  const copySourceSales = mode === 'create' ? (prefill?.sourceLinkedSalesId ?? null) : null;
  /** 원본 발주서의 관련 영업건 전부(v2.284.0) */
  const copySourceAll = mode === 'create'
    ? (prefill?.sourceLinkedSalesIds?.length ? prefill.sourceLinkedSalesIds : copySourceSales ? [copySourceSales] : [])
    : [];

  function updateItem(idx: number, patch: Partial<PoItemInput>) {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }
  function removeItem(idx: number) {
    setItems((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== idx)));
  }
  function addItem() {
    setItems((prev) => [...prev, emptyItem()]);
  }
  // 품목 검색에서 항목 선택 — 품목명 채우고, 상세 내용이 비어 있으면 규격으로 채움.
  function pickItem(idx: number, item: PoLookupItem) {
    setItems((prev) =>
      prev.map((it, i) =>
        i === idx
          ? {
              ...it,
              itemName: item.itemName,
              description: it.description.trim() ? it.description : (item.spec ?? ''),
            }
          : it,
      ),
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    const isQuote = poType === 'quote_attached';
    if (isQuote) {
      if (!projectName.trim()) {
        setErr('견적서 첨부형은 건명(프로젝트명/요약)을 입력해 주세요');
        return;
      }
    } else if (items.length === 0 || items.every((it) => !it.itemName && !it.description)) {
      setErr('품목을 1개 이상 입력해 주세요');
      return;
    }
    // 관련 영업건 — 대표 + 더 고른 것(빈 대표면 첫 번째가 대표 · 중복 빼기)
    const allLinked = [linkedSalesId, ...moreSales].map((x) => x.trim()).filter(Boolean)
      .filter((x, i, a) => a.indexOf(x) === i);
    const badId = allLinked.find((x) => !/^\d{4}-(SM|EJ)-\d{4}(-\d{2})?$/.test(x));
    if (badId) {
      setErr(`영업건 매출번호 형식 오류(${badId}) — 예: 2026-SM-0001`);
      return;
    }
    const linked = allLinked[0] ?? '';
    setSubmitting(true);
    try {
      await onSubmit(
        {
          companyId,
          poDate,
          poType,
          totalAmount: isQuote ? (Number(totalAmount) || 0) : undefined,
          recipientSupplier: recipientSupplier.trim(),
          reference: reference.trim(),
          projectName: projectName.trim(),
          deliveryPlace: deliveryPlace.trim(),
          deliveryDue: deliveryDue.trim(),
          paymentTerm: paymentTerm.trim(),
          linkedSalesId: linked || null,
          linkedSalesIds: allLinked,
          remark,
          empName: empDisplay.trim(),
          // 사용자 UI 는 "담당" 한 줄 (직책 포함). DB 의 emp_title 은 빈 문자열로 유지하여
          // PDF 출력 시 "이름 부장 부장" 같은 중복 표기 방지
          empTitle: '',
          contactPhone: contactPhone.trim(),
          contactEmail: contactEmail.trim(),
          items: isQuote
            ? []
            : items.map((it) => ({
                itemName: it.itemName.trim(),
                description: it.description,
                qty: Number(it.qty) || 0,
                unitPrice: Number(it.unitPrice) || 0,
                note: it.note,
              })),
        },
        // 신규 작성 + 견적서 첨부형일 때만 대기 파일 전달 (수정 모드는 라이브 업로드)
        isQuote && mode === 'create' ? pendingAttachments : [],
      );
    } catch (e2) {
      setErr((e2 as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  // 폼 안 어디서든 Enter 키 입력 시 자동 submit 방지 — textarea·submit 버튼만 통과
  // 사용자 의도: 입력 도중 엔터로 저장되지 않고 [저장] 버튼으로만 저장
  function blockEnterSubmit(e: React.KeyboardEvent<HTMLFormElement>) {
    if (e.key !== 'Enter') return;
    const target = e.target as HTMLElement;
    if (target.tagName === 'TEXTAREA') return;
    if (target.tagName === 'BUTTON' && (target as HTMLButtonElement).type === 'submit') return;
    e.preventDefault();
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4">
      <form
        onSubmit={handleSubmit}
        onKeyDown={blockEnterSubmit}
        className="bg-white rounded shadow-xl w-full max-w-5xl my-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h2 className="ui-modal-title">
            {mode === 'create' ? '발주서 작성' : '발주서 수정'}
            {mode === 'create' && prefill && (
              <span className="ml-2 text-sm font-normal text-slate-500">원본 {prefill.sourcePoNo} 복사</span>
            )}
          </h2>
          <div className="flex items-center gap-2">
            {mode === 'create' && (
              <button
                type="button"
                onClick={() => setImportOpen(true)}
                title="매입처에서 받은 견적서를 Claude 로 추출 → 폼 자동 채움 (비용 0)"
                className="btn btn-success btn-sm"
              >
                📋 견적서 자동 채움
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="ui-close"
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* 복사 안내 (「이 내용으로 새 발주서」 · v2.213.0) — 무엇이 원본과 달라졌는지 먼저 알린다 */}
          {mode === 'create' && prefill && (
            <div className="px-3 py-2 bg-sky-50 border border-sky-300 rounded text-xs text-sky-900 space-y-1">
              <div className="font-bold">
                📋 발주서 {prefill.sourcePoNo} 내용을 복사해 왔습니다. 원본은 그대로 남고, 새 발주번호로 저장됩니다. 확인·수정 후 [확인]을 누르세요.
              </div>
              <ul className="list-disc pl-5 space-y-0.5">
                <li>발주일자는 오늘로 바꿨습니다.</li>
                <li>
                  관련 영업건은 비워 두었습니다
                  {prefill.sourceLinkedSalesId
                    ? ' — 원본과 같은 건이면 아래 [같은 영업건으로]를 누르세요.'
                    : ' — 필요하면 아래에서 고르세요.'}
                </li>
                {prefill.poType === 'quote_attached' && (
                  <li>거래처 견적서 파일은 복사되지 않습니다 — 아래에서 새로 첨부해 주세요.</li>
                )}
                {contact && prefill.sourceEmpId && prefill.sourceEmpId !== contact.empId && (
                  <li>담당·연락처·이메일은 작성하는 사람으로 채웠습니다 (원본 담당: {prefill.sourceEmpName}).</li>
                )}
              </ul>
            </div>
          )}

          {/* 발주 방식 선택 */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-slate-600">발주 방식:</span>
            <SegmentedControl ariaLabel="발주 방식" value={poType} onChange={setPoType}
              options={[{ key: 'standard', label: '① 품목 직접 입력' }, { key: 'quote_attached', label: '② 견적서 첨부형' }]} />
            {poType === 'quote_attached' && (
              <span className="text-xs text-slate-500">
                거래처 견적서를 첨부하고, 발주서엔 건명·총액만 적습니다 (다운로드 시 한 PDF로 합쳐짐)
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <Field label="사업자">
              <select
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value as PoCompany)}
                className="ui-input w-full"
              >
                <option value="SM">SM — (주)에스엠시스템즈</option>
                <option value="EJ">EJ — (주)이진아이씨티</option>
              </select>
            </Field>
            <Field label="발주일자">
              <input
                type="date"
                value={poDate}
                onChange={(e) => setPoDate(e.target.value)}
                className="ui-input w-full"
              />
            </Field>
            <Field label="담당">
              <input
                type="text"
                value={empDisplay}
                onChange={(e) => setEmpDisplay(e.target.value)}
                placeholder={contact?.displayName ?? '담당자 이름·직책'}
                className="ui-input w-full"
              />
            </Field>
            <Field label="연락처">
              <input
                type="text"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                placeholder={contact?.phone ?? '010-xxxx-xxxx'}
                className="ui-input w-full"
              />
            </Field>
            <Field label="이메일">
              <input
                type="text"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder={contact?.email ?? 'email@smsys.co.kr'}
                className="ui-input w-full"
              />
            </Field>
            <Field label="수신 (매입처)">
              <SimpleAutocomplete
                value={recipientSupplier}
                onChange={setRecipientSupplier}
                candidates={supplierOptions}
                placeholder="예: 한결피아이에프 (매입에서 검색 또는 직접 입력)"
              />
            </Field>
            <Field label="참조 (매입처 담당자)">
              <input
                type="text"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="예: 김근영 팀장님 (선택)"
                className="ui-input w-full"
              />
            </Field>
            <Field label="프로젝트명">
              <input
                type="text"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="예: 강남구 고도화 사업"
                className="ui-input w-full"
              />
            </Field>
            <Field label="납품장소">
              <input
                type="text"
                value={deliveryPlace}
                onChange={(e) => setDeliveryPlace(e.target.value)}
                className="ui-input w-full"
              />
            </Field>
            <Field label="납품기한">
              <input
                type="text"
                value={deliveryDue}
                onChange={(e) => setDeliveryDue(e.target.value)}
                placeholder="예: 협의 / 발주 후 2주 등"
                className="ui-input w-full"
              />
            </Field>
            <Field label="결제조건">
              <input
                type="text"
                value={paymentTerm}
                onChange={(e) => {
                  setPaymentTerm(e.target.value);
                  setTermAuto(null); // 직접 고치면 '자동 입력' 표시는 뗀다
                  setTermFromCopy(false);
                }}
                className="ui-input w-full"
              />
              {termAuto && (
                <div className="mt-1 text-xs text-emerald-700">
                  💳 [{termAuto}] 에 등록된 결제조건을 불러왔습니다
                </div>
              )}
              {mode === 'create' && termFromCopy && (
                <div className="mt-1 text-xs text-sky-700">
                  📋 원본 발주서의 결제조건 그대로입니다 — 매입처를 바꾸면 그 매입처 기준으로 다시 채웁니다
                </div>
              )}
            </Field>
            <Field label="관련 영업건 (선택)">
              <div className="flex gap-1.5">
                <input
                  type="text"
                  value={linkedSalesId}
                  onChange={(e) => {
                    setLinkedSalesId(e.target.value);
                    setLinkedSalesLabel('');
                  }}
                  placeholder="검색 또는 매출번호 직접 입력"
                  className="ui-input flex-1 font-mono"
                />
                <button
                  type="button"
                  onClick={() => { setPickerAdd(false); setPickerOpen(true); }}
                  className="btn btn-outline"
                  title="사업명·거래처·엔드유저 등으로 영업건 검색"
                >
                  🔍 검색
                </button>
                {linkedSalesId && (
                  <button
                    type="button"
                    onClick={() => {
                      setLinkedSalesId('');
                      setLinkedSalesLabel('');
                    }}
                    className="btn btn-secondary"
                    title="선택 해제"
                  >
                    ×
                  </button>
                )}
              </div>
              {linkedSalesLabel && (
                <div className="mt-1 text-xs text-slate-500 truncate" title={linkedSalesLabel}>
                  ↳ {linkedSalesLabel}
                </div>
              )}
              {/* 복사 원본의 영업건 — 자동으로 넣지 않고 한 번 누르면 넣게 한다 (v2.213.0 · po-api poDetailToDraft 참고).
                  프로젝트명은 건드리지 않는다(원본에서 손질한 이름 그대로). */}
              {copySourceAll.length > 0 && !linkedSalesId.trim() && moreSales.length === 0 && (
                <div className="mt-1 text-xs text-slate-500">
                  📋 원본 발주서는 <span className="font-mono">{copySourceAll.join(', ')}</span> 에 연결돼 있었습니다
                  <button
                    type="button"
                    onClick={() => {
                      setLinkedSalesId(copySourceAll[0]!);
                      setMoreSales(copySourceAll.slice(1));
                      setLinkedSalesLabel('원본 발주서와 같은 영업건');
                    }}
                    className="btn btn-outline btn-sm ml-1.5"
                  >
                    같은 영업건으로
                  </button>
                </div>
              )}
              {/* 영업건 여러 개 (v2.284.0 · 개발요청 #5) — 위 칸 = 대표(NAS 보관 폴더 · [매입 연동] 기본값), 아래 = 더 고른 것.
                  품목마다 어느 영업건인지는 [매입 연동] 창에서 정한다. */}
              {moreSales.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {moreSales.map((sid) => (
                    <span key={sid} className="inline-flex items-center gap-1 rounded border border-slate-300 bg-slate-50 px-1.5 py-0.5 text-xs">
                      <span className="font-mono">{sid}</span>
                      <button type="button" className="text-sky-700 hover:underline" title="이 영업건을 대표로"
                        onClick={() => {
                          const cur = linkedSalesId.trim();
                          setMoreSales((m) => [...(cur ? [cur] : []), ...m.filter((x) => x !== sid)]);
                          setLinkedSalesId(sid);
                          setLinkedSalesLabel('');
                        }}>대표로</button>
                      <button type="button" className="text-slate-500 hover:text-red-600" aria-label={`${sid} 빼기`} title="빼기"
                        onClick={() => setMoreSales((m) => m.filter((x) => x !== sid))}>×</button>
                    </span>
                  ))}
                </div>
              )}
              <div className="mt-1.5 flex items-center gap-2">
                <button type="button" className="btn btn-outline btn-sm" onClick={() => { setPickerAdd(true); setPickerOpen(true); }}
                  title="한 발주서에 여러 영업건의 품목이 들어갈 때 — 품목마다 어느 영업건인지는 [매입 연동] 창에서 정합니다">
                  + 영업건 추가
                </button>
                {moreSales.length > 0 && (
                  <span className="text-xs text-slate-500">위 칸이 대표 영업건 · 모두 {[linkedSalesId.trim(), ...moreSales].filter(Boolean).length}건</span>
                )}
              </div>
            </Field>
          </div>

          {/* 품목 표 (품목 직접 입력형) */}
          {poType === 'standard' && (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <h3 className="ui-subsection-title">
                품목 ({items.length}건)
                <span className="ml-2 text-xs font-normal text-slate-500">
                  💡 품목칸에서 그룹·품명·규격으로 검색해 선택 (매입처 입력 시 그 매입처 매입 품목만) · 목록에 없으면 직접 작성
                </span>
              </h3>
              <button
                type="button"
                onClick={addItem}
                className="btn btn-outline btn-sm"
              >
                + 품목 추가
              </button>
            </div>
            <div className="overflow-x-auto border border-slate-200 rounded">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-1 py-1.5 w-8 text-center text-slate-600 font-medium">#</th>
                    <th className="px-1 py-1.5 w-44 text-left text-slate-600 font-medium">품목</th>
                    <th className="px-1 py-1.5 text-left text-slate-600 font-medium">상세 내용</th>
                    <th className="px-1 py-1.5 w-16 text-right text-slate-600 font-medium">수량</th>
                    <th className="px-1 py-1.5 w-28 text-right text-slate-600 font-medium">공급단가</th>
                    <th className="px-1 py-1.5 w-28 text-right text-slate-600 font-medium">공급합계</th>
                    <th className="px-1 py-1.5 w-28 text-left text-slate-600 font-medium">비고</th>
                    <th className="px-1 py-1.5 w-8 text-center text-slate-600 font-medium"></th>
                  </tr>
                </thead>
                <tbody ref={itemsBodyRef}>
                  {items.map((it, idx) => (
                    <tr key={idx} className="border-t border-slate-100 align-top">
                      <td className="px-1 py-1 text-center text-slate-500">{idx + 1}</td>
                      <td className="px-1 py-1">
                        <PoItemSearchSelect
                          value={it.itemName}
                          candidates={itemCandidates}
                          supplierFiltered={itemsSupplierFiltered}
                          supplierLabel={recipientSupplier.trim()}
                          loading={itemsLoading}
                          onChange={(v) => updateItem(idx, { itemName: v })}
                          onPick={(item) => pickItem(idx, item)}
                        />
                      </td>
                      <td className="px-1 py-1">
                        <textarea
                          value={it.description}
                          onChange={(e) => updateItem(idx, { description: e.target.value })}
                          placeholder="상세 내용 (여러 줄 가능)"
                          rows={1}
                          className="ui-input ui-input-sm ui-textarea w-full min-h-[1.75rem] py-1 leading-snug resize-none overflow-hidden"
                        />
                      </td>
                      <td className="px-1 py-1">
                        <input
                          type="number"
                          min={0}
                          step={1}
                          value={it.qty}
                          onChange={(e) =>
                            updateItem(idx, { qty: Number(e.target.value) || 0 })
                          }
                          className="ui-input ui-input-sm w-full text-right"
                        />
                      </td>
                      <td className="px-1 py-1">
                        <input
                          type="number"
                          min={0}
                          step={1}
                          value={it.unitPrice}
                          onChange={(e) =>
                            updateItem(idx, { unitPrice: Number(e.target.value) || 0 })
                          }
                          className="ui-input ui-input-sm w-full text-right"
                        />
                      </td>
                      <td className="px-1 py-1 text-right text-slate-700 font-medium">
                        {totals.lineTotals[idx]?.toLocaleString() ?? 0}
                      </td>
                      <td className="px-1 py-1">
                        <input
                          type="text"
                          value={it.note}
                          onChange={(e) => updateItem(idx, { note: e.target.value })}
                          placeholder="비고"
                          className="ui-input ui-input-sm w-full"
                        />
                      </td>
                      <td className="px-1 py-1 text-center">
                        <button
                          type="button"
                          onClick={() => removeItem(idx)}
                          disabled={items.length === 1}
                          className="text-rose-600 hover:text-rose-800 disabled:text-slate-300 text-base leading-none"
                          title="행 삭제"
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50 border-t border-slate-200">
                  <tr>
                    <td colSpan={5} className="px-2 py-1.5 text-right text-xs text-slate-600 font-medium">
                      공급가 총계 (VAT 별도)
                    </td>
                    <td className="px-2 py-1.5 text-right text-sm font-bold text-slate-800">
                      {totals.totalSupply.toLocaleString()}
                    </td>
                    <td colSpan={2}></td>
                  </tr>
                  <tr>
                    <td colSpan={5} className="px-2 py-1.5 text-right text-xs text-slate-600 font-medium">
                      공급가 총계 (VAT 포함)
                    </td>
                    <td className="px-2 py-1.5 text-right text-sm font-bold text-emerald-700">
                      {totals.totalWithVat.toLocaleString()}
                    </td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
          )}

          {/* 견적서 첨부형 본문 — 총액(선택) + 거래처 견적서 첨부 */}
          {poType === 'quote_attached' && (
            <div className="space-y-3">
              <div className="rounded border border-blue-200 bg-blue-50/60 px-3 py-2 text-xs text-blue-800">
                본 발주는 <b>아래 첨부된 거래처 견적서 내용에 따라</b> 발주합니다. 발주서 서식에는 위의 <b>건명(프로젝트명)</b>과 아래 <b>총액</b>만 표기되고,
                다운로드 시 <b>견적서(PDF)가 발주서 뒤에 합쳐진 한 PDF</b>로 받습니다. (엑셀 견적서는 합쳐지지 않고 별도 파일로 함께 첨부됩니다)
              </div>
              <div className="max-w-xs">
                <label className="ui-label block mb-0.5">발주 총액 (VAT 포함, 선택)</label>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={totalAmount || ''}
                  onChange={(e) => setTotalAmount(Number(e.target.value) || 0)}
                  placeholder="비우면 '첨부 견적서 참조'로 표기"
                  className="ui-input w-full text-right"
                />
                {totalAmount > 0 && (
                  <div className="mt-1 text-xs text-slate-500 text-right">
                    공급가(VAT 별도) 약 {Math.round(totalAmount / 1.1).toLocaleString()} 원
                  </div>
                )}
              </div>
              {mode === 'edit' && initial ? (
                <AttachmentSection
                  mode="live"
                  parentKind="po"
                  parentId={String(initial.id)}
                  kind="견적서"
                  title="거래처 견적서 (PDF는 발주서와 합쳐짐 · 엑셀은 별도 첨부)"
                  accept={QUOTE_ACCEPT}
                />
              ) : (
                <AttachmentSection
                  mode="pending"
                  files={pendingAttachments}
                  onChange={setPendingAttachments}
                  title="거래처 견적서 (PDF는 발주서와 합쳐짐 · 엑셀은 별도 첨부)"
                  accept={QUOTE_ACCEPT}
                />
              )}
            </div>
          )}

          {/* Remark */}
          <Field label="Remark" wide>
            <textarea
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              rows={3}
              placeholder="비고/특이사항 (선택)"
              className="ui-input ui-textarea w-full resize-y"
            />
          </Field>

          {err && (
            <div className="px-3 py-2 bg-rose-50 border border-rose-200 text-rose-800 rounded text-xs">
              {err}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary"
          >
            취소
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="btn btn-primary"
          >
            {submitting ? '저장 중...' : mode === 'create' ? '확인' : '수정'}
          </button>
        </div>
      </form>
      <SalesPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(row) => {
          if (pickerAdd) {
            // 하나 더 추가 — 대표·프로젝트명은 그대로(대표가 비어 있으면 대표로)
            const cur = linkedSalesId.trim();
            if (!cur) { setLinkedSalesId(row.salesId); return; }
            if (row.salesId !== cur) setMoreSales((m) => (m.includes(row.salesId) ? m : [...m, row.salesId]));
            return;
          }
          setMoreSales((m) => m.filter((x) => x !== row.salesId));
          setLinkedSalesId(row.salesId);
          const parts = [row.proposalCompany, row.endUser, row.projectName]
            .filter((x) => x && x.trim() !== '')
            .join(' / ');
          setLinkedSalesLabel(parts || row.salesId);
          /* ⭐**영업건을 고르면 프로젝트명을 가져온다** (v2.163.0).
           *   전에는 영업건만 연결하고 프로젝트명 칸은 그대로 비워 둬서, 그 빈칸이
           *   **발주서 PDF·파일 이름·거기서 만든 매입까지** 그대로 비어서 흘러갔다(운영 실측).
           *   ⚠**사람이 적어 둔 값은 덮지 않는다** — 비어 있을 때, 또는 아직 내가 채운 그대로일 때만. */
          const fromSales = (row.projectName ?? '').trim();
          const typed = projectName.trim();
          if (fromSales && (typed === '' || typed === autoProjectRef.current)) {
            setProjectName(fromSales);
            autoProjectRef.current = fromSales;
          }
        }}
        defaultCompanyId={companyId}
        initialQuery={pickerAdd ? undefined : (linkedSalesId.trim() || undefined)}
      />
      <QuoteImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onApply={applyImported}
        current={{
          recipientSupplier,
          projectName,
          poType,
          itemCount: items.filter((it) => it.itemName.trim() || it.description.trim()).length,
        }}
        knownSuppliers={supplierOptions}
      />
    </div>
  );
}

function Field({
  label,
  children,
  wide,
}: {
  label: string;
  children: React.ReactNode;
  wide?: boolean;
}) {
  return (
    <div className={wide ? 'md:col-span-2' : ''}>
      <label className="ui-label block mb-0.5">{label}</label>
      {children}
    </div>
  );
}
